// US-11 — /settings rediseñada (UC-11.08)
import { createFileRoute } from "@tanstack/react-router";
import { Activity, AlertTriangle } from "lucide-react";
import { DemoToggle } from "@/components/topbar/DemoToggle";
import { ThemeToggle } from "@/components/topbar/ThemeToggle";
import { useDemoMode } from "@/hooks/useDemoMode";
import { useTheme } from "@/hooks/useTheme";
import { useSignOut } from "@/hooks/useSignOut";
import { useMcpHealth } from "@/hooks/useMcpHealth";
import { useConnectedAccounts, type AccountProvider } from "@/hooks/useConnectedAccounts";
import { useAuditLog, type AuditLogEntry } from "@/hooks/useAuditLog";
import { HqLinkedAccountsCard } from "@/components/settings/HqLinkedAccountsCard";
// Mantenemos el import comentado por si volvemos al flujo bulk.
import { WeeklyCapacityCard } from "@/components/settings/WeeklyCapacityCard";
import { FiscalProfileCard } from "@/components/settings/FiscalProfileCard";
import { IrpfBracketsCard } from "@/components/settings/IrpfBracketsCard";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  ConfirmDialog,
  DataTable,
  EmptyState,
  StatusPill,
  type DataTableColumn,
} from "@/components/ui/v2";
import { env } from "@/lib/env";
import { formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";

const PROVIDER_LABEL: Record<AccountProvider, string> = {
  google: "Gmail",
  google_calendar: "Google Calendar",
  outlook: "Outlook",
  facturascripts: "FacturaScripts",
  strava: "Strava",
};

function SettingsPage() {
  const { theme } = useTheme();
  const { demoMode } = useDemoMode();
  const signOut = useSignOut();
  const { health } = useMcpHealth();
  const { accounts, isLoading: accountsLoading } = useConnectedAccounts();
  const { items: auditItems, isLoading: auditLoading } = useAuditLog(50);

  const auditColumns: DataTableColumn<AuditLogEntry>[] = [
    {
      key: "created_at",
      header: "Timestamp",
      accessor: (r) => new Date(r.created_at).toISOString().replace("T", " ").slice(0, 19),
      mono: true,
      width: "180px",
    },
    { key: "tool_name", header: "Tool", accessor: (r) => <span className="font-mono text-fg">{r.tool_name}</span> },
    {
      key: "success",
      header: "Estado",
      accessor: (r) =>
        r.success ? (
          <StatusPill status="paid" label="Success" />
        ) : (
          <StatusPill status="overdue" label={r.error_class ?? "Error"} />
        ),
      width: "120px",
    },
    {
      key: "duration_ms",
      header: "ms",
      accessor: (r) => <span className="font-mono tabular text-fg-secondary">{r.duration_ms}</span>,
      width: "80px",
    },
  ];

  return (
    <div className="flex flex-col gap-6 max-w-4xl">
      <header>
        <h1 className="text-lg font-semibold tracking-tight text-fg">Ajustes</h1>
        <p className="text-sm text-fg-secondary mt-1">
          Configuración de HQ, cuentas conectadas y seguridad.
        </p>
      </header>

      {/* Apariencia */}
      <Card padding="lg">
        <CardHeader>
          <div>
            <CardTitle>Apariencia</CardTitle>
            <CardDescription>Tema y modo de privacidad</CardDescription>
          </div>
        </CardHeader>
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4 py-2 border-b border-default last:border-b-0">
            <div>
              <p className="text-sm font-medium text-fg">Tema</p>
              <p className="text-xs text-fg-secondary mt-0.5">
                Light por defecto. Activo: <span className="text-fg tabular">{theme}</span>.
              </p>
            </div>
            <ThemeToggle />
          </div>
          <div className="flex items-center justify-between gap-4 py-2">
            <div>
              <p className="text-sm font-medium text-fg">Modo privacidad</p>
              <p className="text-xs text-fg-secondary mt-0.5">
                Oculta importes y nombres en pantalla. Útil para compartir pantalla en reuniones. Los datos en BD y red no se tocan, solo el render.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-fg-tertiary tabular">{demoMode ? "ON" : "OFF"}</span>
              <DemoToggle />
            </div>
          </div>
        </div>
      </Card>

      {/* Cuentas conectadas */}
      <Card padding="lg">
        <CardHeader>
          <div>
            <CardTitle>Cuentas conectadas</CardTitle>
            <CardDescription>Servicios externos vinculados al MCP</CardDescription>
          </div>
        </CardHeader>
        {accountsLoading ? (
          <p className="text-sm text-fg-tertiary py-3">Cargando…</p>
        ) : accounts.length === 0 ? (
          <EmptyState
            padding="md"
            title="Sin cuentas conectadas"
            description="Cuando vincules Gmail, Outlook u otros servicios aparecerán aquí."
          />
        ) : (
          <ul className="flex flex-col">
            {accounts.map((account) => (
              <li
                key={account.id}
                className="flex items-center justify-between gap-3 py-3 border-b border-default last:border-b-0"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className={cn(
                      "inline-block size-2 rounded-full shrink-0",
                      account.active ? "bg-success" : "bg-danger",
                    )}
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-fg truncate">{PROVIDER_LABEL[account.provider] ?? account.provider}</p>
                    <p className="text-xs text-fg-tertiary truncate font-mono tabular">{account.label}</p>
                    {account.detail ? (
                      <p className="text-xs text-fg-tertiary truncate">{account.detail}</p>
                    ) : null}
                  </div>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <span className="text-xs text-fg-tertiary tabular">
                    {account.lastSyncAt ? formatRelative(account.lastSyncAt) : "—"}
                  </span>
                  <button
                    type="button"
                    disabled
                    title="Disponible en próxima iteración"
                    className="inline-flex items-center justify-center h-7 px-2 rounded-md text-xs font-medium border border-default text-fg hover:bg-subtle transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Desconectar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Cuentas conectadas al HQ (US-14) — separated from the MCP-session view */}
      <HqLinkedAccountsCard />

      {/* US-16: capacidad semanal */}
      <WeeklyCapacityCard />

      {/* US-26: perfil fiscal (IRPF objetivo + tramos) */}
      <section aria-label="Perfil fiscal" className="flex flex-col gap-4">
        <FiscalProfileCard />
        <IrpfBracketsCard />
      </section>

      {/* UC-13.04: Backfill billing (admin) — US-19: reemplazado por fetch
          on-demand desde el InvoicePdfViewer. Mantenemos el componente
          latente por si volvemos a necesitar el bulk. */}

      {/* Privacidad */}
      <Card padding="lg">
        <CardHeader>
          <div>
            <CardTitle>Privacidad</CardTitle>
            <CardDescription>Whitelist single-tenant + persistencia demo</CardDescription>
          </div>
        </CardHeader>
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3 py-2 border-b border-default">
            <div>
              <p className="text-sm font-medium text-fg">Email autorizado</p>
              <p className="text-xs text-fg-tertiary mt-0.5">Single-tenant. Enforced server-side via RLS.</p>
            </div>
            <code className="px-2.5 py-1 rounded-sm bg-subtle border border-default text-xs font-mono tabular text-fg">
              {env.VITE_ALLOWED_EMAIL}
            </code>
          </div>
          <div className="flex items-center justify-between gap-3 py-2">
            <label htmlFor="remember-demo" className="text-sm text-fg font-medium">
              Recordar modo privacidad entre sesiones
            </label>
            <input
              id="remember-demo"
              type="checkbox"
              defaultChecked
              className="size-4 rounded border-strong bg-surface accent-accent"
            />
          </div>
        </div>
      </Card>

      {/* Cuenta */}
      <Card padding="lg">
        <CardHeader>
          <div>
            <CardTitle>Cuenta</CardTitle>
            <CardDescription>Información técnica y sesión</CardDescription>
          </div>
        </CardHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-md bg-subtle border border-default">
            <p className="text-xs uppercase tracking-[0.04em] text-fg-tertiary mb-1">Plan</p>
            <p className="text-sm font-mono tabular text-fg font-semibold">Personal · v1</p>
          </div>
          <div className="p-3 rounded-md bg-subtle border border-default">
            <p className="text-xs uppercase tracking-[0.04em] text-fg-tertiary mb-1">MCP version</p>
            <p
              className={cn(
                "text-sm font-mono tabular font-semibold",
                health?.ok ? "text-success" : "text-fg-tertiary",
              )}
              title={health?.ok ? "MCP responde sano en /healthz" : "Sin respuesta del MCP — ¿offline o CORS?"}
            >
              {health ? `v${health.version}` : "—"}
            </p>
          </div>
          <div className="p-3 rounded-md bg-subtle border border-default col-span-2">
            <p className="text-xs uppercase tracking-[0.04em] text-fg-tertiary mb-1">URLs del servicio</p>
            <p className="text-xs font-mono tabular text-fg-secondary">{env.VITE_API_BASE_URL}</p>
            <p className="text-xs font-mono tabular text-fg-secondary">{env.VITE_PUBLIC_BASE_URL}</p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-default">
          <div className="flex items-start gap-3">
            <AlertTriangle className="size-4 text-danger mt-0.5" strokeWidth={1.5} aria-hidden />
            <div>
              <p className="text-sm font-medium text-fg">Cerrar sesión en todos los dispositivos</p>
              <p className="text-xs text-fg-tertiary mt-0.5">Revoca todos los tokens activos.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={signOut.requestSignOut}
            disabled={signOut.signingOut}
            className="inline-flex items-center justify-center h-8 px-3 rounded-md text-sm font-medium bg-danger-soft text-danger border border-danger/20 hover:bg-danger hover:text-white transition-colors duration-fast disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger/40"
          >
            {signOut.signingOut ? "Cerrando…" : "Cerrar sesión"}
          </button>
        </div>
      </Card>

      {/* Actividad reciente */}
      <Card padding="lg">
        <CardHeader>
          <div>
            <CardTitle>Actividad reciente</CardTitle>
            <CardDescription>
              Últimas operaciones del MCP, la API y workflows externos
            </CardDescription>
          </div>
        </CardHeader>
        {auditLoading ? (
          <p className="text-sm text-fg-tertiary py-3">Cargando…</p>
        ) : auditItems.length === 0 ? (
          <EmptyState
            padding="md"
            icon={Activity}
            title="Sin actividad reciente"
            description="Cuando el MCP, la API o un workflow registren actividad la verás aquí. Trazabilidad multi-source planificada en US-12."
          />
        ) : (
          <DataTable
            ariaLabel="Audit log"
            columns={auditColumns}
            data={auditItems}
            rowKey={(r) => String(r.id)}
            stickyHeader={false}
          />
        )}
      </Card>

      <ConfirmDialog
        open={signOut.open}
        onClose={signOut.cancel}
        onConfirm={signOut.confirm}
        loading={signOut.signingOut}
        tone="danger"
        title="¿Cerrar sesión?"
        description="Se revocará la sesión actual y volverás a la pantalla de inicio."
        confirmLabel="Cerrar sesión"
        cancelLabel="Cancelar"
      />
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/settings")({
  component: SettingsPage,
});
